import { createAsyncThunk } from '@reduxjs/toolkit';
import { getLookup, searchProductOptions } from '../../services/repositories/lookupsService.ts';
import type { LookupName } from '../../services/repositories/lookupsService.ts';
import type { LookupsRootState } from '../slices/lookupsSlice.ts';
import { requestKey } from '../requests.ts';

export const LOOKUP_CACHE_MS = 10 * 60 * 1000;
export const DEFAULT_LOOKUP_SCOPE = 'global';

export interface LookupRequestArgs {
  name: LookupName;
  scope?: string;
  force?: boolean;
}

export interface ProductLookupArgs {
  term: string;
  pslId?: number | string;
  scope?: string;
  force?: boolean;
}

export const lookupRequestKey = ({ name, scope = DEFAULT_LOOKUP_SCOPE }: LookupRequestArgs) =>
  requestKey({ name, scope });

export const productLookupKey = ({ term, pslId, scope = DEFAULT_LOOKUP_SCOPE }: ProductLookupArgs) =>
  requestKey({ term, pslId: pslId === undefined ? undefined : String(pslId), scope });

export const fetchLookup = createAsyncThunk(
  'lookups/fetchLookup',
  async (args: LookupRequestArgs, { signal }) => getLookup(args.name, signal),
  {
    condition: (args, { getState }) => {
      const request = (getState() as LookupsRootState).lookups.lists[lookupRequestKey(args)];
      return args.force || !request?.data || Date.now() - request.updatedAt >= LOOKUP_CACHE_MS;
    },
  },
);

export const fetchProductOptions = createAsyncThunk(
  'lookups/fetchProductOptions',
  async (args: ProductLookupArgs, { signal }) => searchProductOptions(args.term, args.pslId, signal),
  {
    condition: (args, { getState }) => {
      if (args.term.length < 2) return false;
      const request = (getState() as LookupsRootState).lookups.products[productLookupKey(args)];
      return args.force || !request?.data || Date.now() - request.updatedAt >= LOOKUP_CACHE_MS;
    },
  },
);